'use client';

export type OAuthBeginResult = {
    pendingToken: string;
    existingUser: boolean;
    profile: { email: string; firstName: string; lastName: string };
};

const socialBtnClass =
    'flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60';

function randomNonce() {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function SocialAuthButtons({
    mode,
    labels,
    onError,
}: {
    mode: 'signin' | 'signup';
    labels: { google: string; linkedin: string; orEmail: string; googleOriginError?: string };
    onBegin: (result: OAuthBeginResult) => void;
    onError: (message: string) => void;
}) {
    const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || '';

    const startGoogle = () => {
        if (!clientId) {
            onError(labels.googleOriginError?.replaceAll('{origin}', window.location.origin) || 'Google no está configurado');
            return;
        }
        const redirectUri = `${window.location.origin}/auth/oauth/google/callback`;
        const nonce = randomNonce();
        try {
            sessionStorage.setItem('oauth_intent', mode);
            sessionStorage.setItem('oauth_google_nonce', nonce);
        } catch { /* private mode */ }
        const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
        url.searchParams.set('client_id', clientId);
        url.searchParams.set('redirect_uri', redirectUri);
        url.searchParams.set('response_type', 'id_token');
        url.searchParams.set('scope', 'openid email profile');
        url.searchParams.set('nonce', nonce);
        url.searchParams.set('state', mode);
        url.searchParams.set('prompt', 'select_account');
        window.location.href = url.toString();
    };

    const startLinkedIn = async () => {
        try {
            try { sessionStorage.setItem('oauth_intent', mode); } catch { /* */ }
            const res = await fetch(`/api/auth/oauth/linkedin/start?state=${mode}`);
            const data = await res.json();
            if (!data.url) {
                onError(data.message || 'LinkedIn no está configurado');
                return;
            }
            window.location.href = data.url;
        } catch {
            onError('LinkedIn no está configurado');
        }
    };

    return (
        <div className="space-y-3">
            <div className="relative py-1">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-2 text-gray-400">{labels.orEmail}</span>
                </div>
            </div>
            {clientId && (
                <button type="button" onClick={startGoogle} className={socialBtnClass}>
                    <GoogleIcon />
                    {labels.google}
                </button>
            )}
            <button type="button" onClick={startLinkedIn} className={socialBtnClass}>
                <LinkedInIcon />
                {labels.linkedin}
            </button>
        </div>
    );
}

function GoogleIcon() {
    return (
        <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden>
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
    );
}

function LinkedInIcon() {
    return (
        <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden>
            <path fill="#0A66C2" d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.47-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.23 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.46C23.2 24 24 23.23 24 22.27V1.73C24 .77 23.2 0 22.23 0z" />
        </svg>
    );
}
